import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import { Globe, Check, ChevronDown } from 'lucide-react';

/**
 * Multilingual Language Switcher
 * 
 * Variants:
 * - 'default': Full dropdown with labels
 * - 'minimal': Compact toggle (cycles through enabled languages)
 * - 'compact': Small dropdown with flags only
 * - 'full': Shows all supported languages (not just enabled)
 */
export default function LanguageSwitcher({ 
  variant = 'default', 
  showLabel = true,
  showAllLanguages = false 
}) {
  const { 
    locale, 
    setLanguage, 
    cycleLanguage,
    getLanguageInfo,
    isLoaded,
    SUPPORTED_LANGUAGES,
    ENABLED_LANGUAGES
  } = useLanguage();

  if (!isLoaded) return null;

  const currentLang = getLanguageInfo(locale);
  
  // Determine which languages to show
  const languagesToShow = showAllLanguages 
    ? SUPPORTED_LANGUAGES 
    : SUPPORTED_LANGUAGES.filter(l => ENABLED_LANGUAGES.includes(l.code));

  // Minimal variant - just cycles through languages on click
  if (variant === 'minimal') {
    return (
      <Button
        variant="ghost"
        size="sm"
        onClick={cycleLanguage}
        className="text-gray-600 hover:text-gray-900 gap-1"
        data-testid="language-toggle"
        title={`${currentLang.nativeName} - Click to change`}
      >
        <Globe className="w-4 h-4" />
        <span>{currentLang.flag}</span>
      </Button>
    );
  }

  // Compact variant - small dropdown with flags
  if (variant === 'compact') {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button 
            variant="ghost" 
            size="sm"
            className="gap-1 px-2"
            data-testid="language-switcher"
          >
            <span className="text-lg">{currentLang.flag}</span>
            <ChevronDown className="w-3 h-3 opacity-50" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-[120px]">
          {languagesToShow.map((lang) => (
            <DropdownMenuItem
              key={lang.code}
              onClick={() => setLanguage(lang.code)}
              className="flex items-center justify-between cursor-pointer"
              data-testid={`lang-option-${lang.code}`}
              disabled={!ENABLED_LANGUAGES.includes(lang.code)}
            >
              <span className="flex items-center gap-2">
                <span className="text-lg">{lang.flag}</span>
                <span className="text-sm">{lang.nativeName}</span>
              </span>
              {locale === lang.code && (
                <Check className="w-4 h-4 text-green-600" />
              )}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  // Full variant - shows all languages including disabled ones
  if (variant === 'full') {
    const enabledLangs = SUPPORTED_LANGUAGES.filter(l => ENABLED_LANGUAGES.includes(l.code));
    const comingSoonLangs = SUPPORTED_LANGUAGES.filter(l => !ENABLED_LANGUAGES.includes(l.code));
    
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button 
            variant="outline" 
            size="sm"
            className="flex items-center gap-2"
            data-testid="language-switcher"
          >
            <Globe className="w-4 h-4" />
            <span>{currentLang.flag}</span>
            <span>{currentLang.nativeName}</span>
            <ChevronDown className="w-3 h-3 opacity-50" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-[180px]">
          <DropdownMenuLabel className="text-xs text-gray-500">
            Available Languages
          </DropdownMenuLabel>
          {enabledLangs.map((lang) => (
            <DropdownMenuItem
              key={lang.code}
              onClick={() => setLanguage(lang.code)}
              className="flex items-center justify-between cursor-pointer"
              data-testid={`lang-option-${lang.code}`}
            >
              <span className="flex items-center gap-2">
                <span className="text-lg">{lang.flag}</span>
                <span>{lang.nativeName}</span>
              </span>
              {locale === lang.code && (
                <Check className="w-4 h-4 text-green-600" />
              )}
            </DropdownMenuItem>
          ))}
          
          {comingSoonLangs.length > 0 && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-xs text-gray-400">
                Coming Soon
              </DropdownMenuLabel>
              {comingSoonLangs.map((lang) => (
                <DropdownMenuItem
                  key={lang.code}
                  disabled
                  className="flex items-center gap-2 opacity-50 cursor-not-allowed"
                >
                  <span className="text-lg">{lang.flag}</span>
                  <span>{lang.nativeName}</span>
                </DropdownMenuItem>
              ))}
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  // Default variant - standard dropdown
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="outline" 
          size="sm"
          className="flex items-center gap-2"
          data-testid="language-switcher"
        >
          <Globe className="w-4 h-4" />
          {showLabel ? (
            <>
              <span>{currentLang.flag}</span>
              <span>{currentLang.nativeName}</span>
            </>
          ) : (
            <span className="text-lg">{currentLang.flag}</span>
          )}
          <ChevronDown className="w-3 h-3 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[160px]">
        {languagesToShow.map((lang) => {
          const isEnabled = ENABLED_LANGUAGES.includes(lang.code);
          return (
            <DropdownMenuItem
              key={lang.code}
              onClick={() => isEnabled && setLanguage(lang.code)}
              className={`flex items-center justify-between ${isEnabled ? 'cursor-pointer' : 'opacity-50 cursor-not-allowed'}`}
              data-testid={`lang-option-${lang.code}`}
            >
              <span className="flex items-center gap-2">
                <span className="text-lg">{lang.flag}</span>
                <span>{lang.nativeName}</span>
              </span>
              {locale === lang.code && (
                <Check className="w-4 h-4 text-green-600" />
              )}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
